import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import TabNavigation from './TabNavigation';
import FilterPanel from './FilterPanel';
import SessionCard from './SessionCard';
import TimeZoneSelector from './TimeZoneSelector';
import EventRibbon from './EventRibbon';
import SurveyPopup from './SurveyPopup';
import VideoPopup from './VideoPopup';
import ArchivedSessionsTable from './ArchivedSessionsTable';
import AgendaWizard from './AgendaWizard';
import ApiService, { API_BASE_URL } from '../services/ApiService';
import { useCategories } from '../hooks/useCategories';
import { getCookie, setCookie } from '../utils/cookies';
import { isSegmentEnabled, matchesSegment, normalizeSegment } from '../utils/segments';
import { getSessionInterval } from '../utils/sessionTime';
import { formatEventDate, getEventDateKey } from '../utils/eventDate';
import videoIcon from '../assets/OPSSWLearningDay_Video_.svg';
import UserMenu from './UserMenu';
import './Dashboard.css';

/**
 * Dashboard Component
 * Main container matching mockUI.html layout exactly
 */
const SEGMENT_PATHS = {
  OPS_SW: '/ops-sw',
  GROW_PLM: '/grow-plm',
  LCS: '/lcs',
  ME: '/me',
  PES: '/pes',
  SIM: '/sim',
};

const Dashboard = ({ initialSegment = null }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => getCookie('tab', 'live'));
  const [sessions, setSessions] = useState([]);
  const [quarterlyEvents, setQuarterlyEvents] = useState([]);
  const [activeEvent, setActiveEvent] = useState(undefined);
  const [selectedEventDate, setSelectedEventDate] = useState(null);
  const [selectedSegment, setSelectedSegment] = useState(() => normalizeSegment(initialSegment || getCookie('segment')));
  const previousInitialSegment = useRef(initialSegment);
  const [selectedCategory, setSelectedCategory] = useState(() => getCookie('category'));
  const [searchQuery, setSearchQuery] = useState('');
  const [searchBy, setSearchBy] = useState('name');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [selectedTimezone, setSelectedTimezone] = useState(() => getCookie('timezone', 'local'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lightweightMode, setLightweightMode] = useState(false);
  const [segmentHubLinks, setSegmentHubLinks] = useState({});
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoActive, setLogoActive] = useState(true);
  const [logoHeight, setLogoHeight] = useState(54);
  const [logoMaxWidth, setLogoMaxWidth] = useState(280);
  const [videoTourUrl, setVideoTourUrl] = useState('https://connect.industrysoftware.automation.siemens.com/platform-tour');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardActive, setWizardActive] = useState(true);
  const { list: categoryList, byCode: categoryColors } = useCategories();

  const normalize24HourTimeInput = (value) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 4);
    if (digitsOnly.length <= 2) return digitsOnly;
    return `${digitsOnly.slice(0, 2)}:${digitsOnly.slice(2)}`;
  };

  const isValid24HourTime = (time) => /^([01]\d|2[0-3]):([0-5]\d)$/.test(time);

  // Fetch settings (segment hub URLs, video tour URL, and logo) on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await ApiService.getPublicSettings();

        // Map settings to segment hub links
        const links = {
          'OPS-SW': settings.segment_hub_ops_sw_url || '',
          GROW_PLM: settings.segment_hub_grow_plm_url || '',
          LCS: settings.segment_hub_lcs_url || '',
          ME: settings.segment_hub_me_url || '',
          PES: settings.segment_hub_pes_url || '',
          SIM: settings.segment_hub_sim_url || '',
        };
        setSegmentHubLinks(links);

        if (settings.video_tour_url) {
          setVideoTourUrl(settings.video_tour_url);
        }

        // Handle logo settings
        if (settings.homepage_logo_url) {
          let logoUrl = settings.homepage_logo_url;
          // If the URL is a relative /static/ path, make it absolute via the API base
          if (logoUrl.startsWith('/static/')) {
            const apiBase = API_BASE_URL.replace(/\/api\/?$/, '');
            logoUrl = apiBase ? `${apiBase}${logoUrl}` : logoUrl;
          }
          setLogoUrl(logoUrl);
        }
        
        // Set logo active status (default to true if not specified)
        const logoActive = settings.homepage_logo_active !== 'false';
        setLogoActive(logoActive);
        // Agenda Wizard visibility (default to true if setting absent)
        setWizardActive(settings.agenda_wizard_active !== 'false');
        if (settings.homepage_logo_height) setLogoHeight(parseInt(settings.homepage_logo_height, 10) || 54);
        if (settings.homepage_logo_max_width) setLogoMaxWidth(parseInt(settings.homepage_logo_max_width, 10) || 280);
      } catch (err) {
        console.error('Failed to fetch settings:', err);
        // Keep default values if fetch fails
      }
    };

    fetchSettings();
  }, []);

  // Segment hub navigation - static fallback (empty, will be configured by Super Admin)
  const segmentHubLinksStatic = {
    'OPS-SW': '',
    GROW_PLM: '',
    LCS: '',
    ME: '',
    PES: '',
    SIM: '',
  };

  const handleHubSegmentChange = (e) => {
    const segment = e.target.value;
    const links = Object.keys(segmentHubLinks).length > 0 ? segmentHubLinks : segmentHubLinksStatic;
    if (segment && links[segment]) {
      window.open(links[segment], '_blank');
      e.target.value = ''; // Reset dropdown
    }
  };

  // Fetch quarterly events and active event on mount
  useEffect(() => {
    const fetchQuarterlyEvents = async () => {
      try {
        const events = await ApiService.getQuarterlyEvents();
        setQuarterlyEvents(events);
      } catch (err) {
        console.error('Failed to fetch quarterly events:', err);
      }
    };

    const fetchActiveEvent = async () => {
      try {
        const event = await ApiService.getActiveEvent();
        setActiveEvent(event);
      } catch (err) {
        console.error('Failed to fetch active event:', err);
      }
    };

    fetchQuarterlyEvents();
    fetchActiveEvent();
  }, []);

  // Fetch sessions when tab or event changes
  useEffect(() => {
    const fetchSessions = async () => {
      // For archived tab, don't fetch until a date is selected
      if (activeTab === 'archived' && !selectedEventDate) {
        setSessions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const { sessions: sessionData, lightweightMode: lwMode } = await ApiService.getSessions(activeTab, selectedEventDate);
        setSessions(sessionData);
        setLightweightMode(lwMode);
      } catch (err) {
        setError(err.message);
        setSessions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, [activeTab, selectedEventDate]);

  // Reset selected segment if it's not enabled for the current event
  useEffect(() => {
    if (!selectedSegment) return;
    let enabledSegs = null;
    if (activeTab === 'archived' && selectedEventDate) {
      const archEvt = quarterlyEvents.find(e => e.eventDate === selectedEventDate);
      enabledSegs = archEvt?.enabledSegments;
    } else if (activeEvent) {
      enabledSegs = activeEvent.enabledSegments;
    }
    if (!isSegmentEnabled(selectedSegment, enabledSegs)) {
      setSelectedSegment(null);
      setCookie('segment', null);
      navigate('/', { replace: true });
    }
  }, [activeEvent, selectedEventDate, activeTab, quarterlyEvents, selectedSegment, navigate]);

  useEffect(() => {
    if (previousInitialSegment.current === initialSegment) return;
    previousInitialSegment.current = initialSegment;
    setSelectedSegment(normalizeSegment(initialSegment));
  }, [initialSegment]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCookie('tab', tab);
    if (tab !== 'archived') {
      setSelectedEventDate(null);
    }
    // Reset filters when changing tabs
    setSelectedSegment(null);
    setSelectedCategory(null);
    setCookie('segment', null);
    setCookie('category', null);
    setSearchQuery('');
    setStartTime('');
    setEndTime('');
  };

  const handleEventDateChange = (eventDate) => {
    setSelectedEventDate(eventDate);
  };

  const handleSegmentChange = (segment) => {
    const code = normalizeSegment(segment);
    setSelectedSegment(code);
    setCookie('segment', code);
    navigate(code ? (SEGMENT_PATHS[code] || '/') : '/', { replace: true });
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCookie('category', category);
  };

  const handleTimezoneChange = (tz) => {
    setSelectedTimezone(tz);
    setCookie('timezone', tz);
  };

  // Client-side filtering
  const filteredSessions = sessions.filter(session => {
    // Segment filter
    if (!matchesSegment(session.segment, selectedSegment)) {
      return false;
    }

    // Category filter
    if (selectedCategory && session.category !== selectedCategory) {
      return false;
    }

    // Search filter
    if (searchBy === 'name' && searchQuery) {
      const query = searchQuery.toLowerCase();
      return session.name.toLowerCase().includes(query) ||
        session.presenter.toLowerCase().includes(query);
    } else if (
      searchBy === 'time' &&
      (isValid24HourTime(startTime) || isValid24HourTime(endTime))
    ) {
      if (!session.timeCET) return false;

      // Extract start time from session (format: HH:MM-HH:MM) — stored in CET
      const [sessionStart] = session.timeCET.split('-');

      const toMinutes = (time) => {
        const [hours, mins] = time.split(':').map(Number);
        return hours * 60 + mins;
      };

      // Convert a user-entered HH:MM (in selected timezone) to CET minutes
      // using the session's own date as reference for DST accuracy
      const userTimeToCETMinutes = (userTime) => {
        try {
          const refDate = session.date; // DD-MM-YYYY
          if (!refDate) return toMinutes(userTime);
          const [day, month, year] = refDate.split('-');
          const [h, m] = userTime.split(':').map(Number);
          const userZone = selectedTimezone === 'local' ? 'local' : selectedTimezone;
          const userDT = DateTime.fromObject(
            { year: parseInt(year), month: parseInt(month), day: parseInt(day), hour: h, minute: m },
            { zone: userZone }
          );
          const cetDT = userDT.setZone('Europe/Paris');
          return cetDT.hour * 60 + cetDT.minute;
        } catch {
          return toMinutes(userTime);
        }
      };

      const sessionStartMins = toMinutes(sessionStart);

      const hasValidStartTime = isValid24HourTime(startTime);
      const hasValidEndTime = isValid24HourTime(endTime);

      // Check if within range
      if (hasValidStartTime && hasValidEndTime) {
        const startMins = userTimeToCETMinutes(startTime);
        const endMins = userTimeToCETMinutes(endTime);
        return sessionStartMins >= startMins && sessionStartMins <= endMins;
      } else if (hasValidStartTime) {
        const startMins = userTimeToCETMinutes(startTime);
        return sessionStartMins >= startMins;
      } else if (hasValidEndTime) {
        const endMins = userTimeToCETMinutes(endTime);
        return sessionStartMins <= endMins;
      }
    }

    return true;
  });

  // Build a set of session keys that belong to Grow@PLM segment
  // Used to show Grow@PLM logo on sessions imported into other segments
  const growPLMSessionKeys = useMemo(() => {
    const keys = new Set();
    sessions.forEach(s => {
      if (s.segment === 'GROW_PLM' || s.segment === 'Grow@PLM') {
        keys.add(`${s.name}||${s.presenter}||${s.timeCET}||${s.date}`);
      }
    });
    return keys;
  }, [sessions]);

  // Deduplicate sessions that appear in multiple segments
  // (same name + presenter + time = same session, keep first occurrence)
  const deduplicatedSessions = filteredSessions.filter((session, index, arr) => {
    return index === arr.findIndex(s =>
      s.name === session.name &&
      s.presenter === session.presenter &&
      s.timeCET === session.timeCET &&
      s.date === session.date
    );
  });

  // Convert time for sidebar display based on timezone
  const convertTimeForSidebar = (timeCET, date) => {
    if (!timeCET || !date) {
      return { display: timeCET ? timeCET.split('-')[0] : 'TBD', showDate: false };
    }

    try {
      const [startTime] = timeCET.split('-');
      const [startHour, startMin] = startTime.split(':');
      const [day, month, year] = date.split('-');

      // Create DateTime in CET
      const startCET = DateTime.fromObject(
        {
          year: parseInt(year),
          month: parseInt(month),
          day: parseInt(day),
          hour: parseInt(startHour),
          minute: parseInt(startMin),
        },
        { zone: 'Europe/Paris' }
      );

      // Convert to selected timezone or local
      const targetZone = selectedTimezone === 'local' ? 'local' : selectedTimezone;
      const converted = startCET.setZone(targetZone);

      // Always show date for archived sessions
      // For live/recommended, show date if different from active event date
      let showDate = activeTab === 'archived';

      if (!showDate && activeEvent && activeEvent.eventDate) {
        // Compare calendar dates without applying the viewer's timezone to the event date.
        showDate = converted.toISODate() !== getEventDateKey(activeEvent.eventDate);
      }

      return {
        display: converted.toFormat('HH:mm'),
        date: showDate ? converted.toFormat('dd MMMM yyyy') : null,
        showDate
      };
    } catch (error) {
      console.error('Time conversion error:', error);
      return { display: timeCET.split('-')[0], showDate: false };
    }
  };

  // Calculate session duration in minutes
  const getSessionDuration = (timeCET) => {
    if (!timeCET || !timeCET.includes('-')) return 60; // default 60 minutes
    try {
      const [start, end] = timeCET.split('-');
      const [startHour, startMin] = start.split(':').map(Number);
      const [endHour, endMin] = end.split(':').map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;
      return endMinutes - startMinutes;
    } catch {
      return 60; // default 60 minutes
    }
  };

  // Build schedule grid data
  const buildScheduleGrid = () => {
    if (deduplicatedSessions.length === 0) return { slots: [], sessionPlacements: [], maxColumns: 0 };

    // Find the global min start and max end across all sessions
    let globalMin = Infinity, globalMax = -Infinity;
    const parsedSessions = deduplicatedSessions.map(session => {
      const { start, end } = getSessionInterval(session.date, session.timeCET);
      const startMins = start.toMillis() / 60000;
      const endMins = end.toMillis() / 60000;
      return { ...session, startMins, endMins, duration: endMins - startMins };
    }).filter(session => Number.isFinite(session.duration) && session.duration > 0);

    if (parsedSessions.length === 0) return { slots: [], sessionPlacements: [], maxColumns: 0 };
    parsedSessions.forEach(({ startMins, endMins }) => {
      if (startMins < globalMin) globalMin = startMins;
      if (endMins > globalMax) globalMax = endMins;
    });

    // Snap globalMin down and globalMax up to 30-min boundaries
    globalMin = Math.floor(globalMin / 30) * 30;
    globalMax = Math.ceil(globalMax / 30) * 30;

    // Generate 30-minute slot labels
    const slots = [];
    for (let minutes = globalMin; minutes < globalMax; minutes += 30) {
      slots.push({
        timestamp: DateTime.fromMillis(minutes * 60000, { zone: 'Europe/Paris' }),
        minutes
      });
    }

    // For each session, compute startSlot and slotSpan
    const sessionItems = parsedSessions.map(session => {
      const startSlot = Math.floor((session.startMins - globalMin) / 30);
      const slotSpan = Math.ceil((session.endMins - session.startMins) / 30);
      return { ...session, startSlot, slotSpan };
    });

    // Sort by startSlot then by slotSpan (longer first for better packing)
    sessionItems.sort((a, b) => a.startSlot - b.startSlot || b.slotSpan - a.slotSpan);

    // Greedy column assignment for overlapping sessions
    // columns[col] = end slot index of last session in that column
    const columns = [];
    const placements = sessionItems.map(session => {
      const endSlot = session.startSlot + session.slotSpan;
      // Find first column where this session fits (no overlap)
      let col = 0;
      while (col < columns.length && columns[col] > session.startSlot) {
        col++;
      }
      if (col >= columns.length) columns.push(0);
      columns[col] = endSlot;
      return { ...session, column: col };
    });

    // Determine which slots are occupied by at least one session
    const occupiedSlotIndices = new Set();
    placements.forEach(session => {
      for (let i = session.startSlot; i < session.startSlot + session.slotSpan; i++) {
        occupiedSlotIndices.add(i);
      }
    });

    // Build a mapping from old slot index to new compacted row index
    const oldToNewRow = {};
    let newRow = 0;
    const compactedSlots = [];
    for (let i = 0; i < slots.length; i++) {
      if (occupiedSlotIndices.has(i)) {
        oldToNewRow[i] = newRow;
        compactedSlots.push(slots[i]);
        newRow++;
      }
    }

    // Remap session placements to compacted grid rows
    const compactedPlacements = placements.map(session => {
      const newStart = oldToNewRow[session.startSlot];
      // Count how many compacted rows this session spans
      let span = 0;
      for (let i = session.startSlot; i < session.startSlot + session.slotSpan; i++) {
        if (occupiedSlotIndices.has(i)) span++;
      }
      return { ...session, startSlot: newStart, slotSpan: span };
    });

    return { slots: compactedSlots, sessionPlacements: compactedPlacements, maxColumns: columns.length };
  };

  const { slots: timeSlots, sessionPlacements, maxColumns } = buildScheduleGrid();

  return (
    <div className="dashboard">
      {/* Header matching mockUI.html exactly */}
      <header className="dashboard-header">
        <div className="header-left">
          {logoActive && logoUrl && (
            <div className="logo-container">
              <img 
                src={logoUrl} 
                alt="Homepage Logo"
                className="homepage-logo"
                style={{ height: `${logoHeight}px`, maxWidth: `${logoMaxWidth}px` }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}
          <div className="hub-dropdown-container">
            <div className="hub-label">Segment Hub Link</div>
            <select className="hub-select-hidden" onChange={handleHubSegmentChange}>
              <option value="" hidden></option>
              <option value="OPS-SW">OPS-SW</option>
              <option value="GROW_PLM">Grow@PLM</option>
              <option value="LCS">LCS</option>
              <option value="ME">ME</option>
              <option value="PES">PES</option>
              <option value="SIM">SIM</option>
            </select>
          </div>
        </div>

        {/* Center Search Bar */}
        <div className="header-center">
          <div className="search-bar">
            <select
              value={searchBy}
              onChange={(e) => {
                setSearchBy(e.target.value);
                setSearchQuery('');
                setStartTime('');
                setEndTime('');
              }}
            >
              <option value="name">Name</option>
              <option value="time">Time</option>
            </select>
            {searchBy === 'name' ? (
              <input
                type="text"
                placeholder="Session Name or Presenter Name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            ) : (
              <>
                <input
                  type="text"
                  placeholder="Start (HH:MM)"
                  value={startTime}
                  onChange={(e) => setStartTime(normalize24HourTimeInput(e.target.value))}
                  onBlur={(e) => {
                    if (e.target.value && !isValid24HourTime(e.target.value)) {
                      setStartTime('');
                    }
                  }}
                  inputMode="numeric"
                  maxLength={5}
                  pattern="^([01]\\d|2[0-3]):([0-5]\\d)$"
                  title="Use 24-hour format HH:MM"
                  className="time-input"
                />
                <span className="time-separator">to</span>
                <input
                  type="text"
                  placeholder="End (HH:MM)"
                  value={endTime}
                  onChange={(e) => setEndTime(normalize24HourTimeInput(e.target.value))}
                  onBlur={(e) => {
                    if (e.target.value && !isValid24HourTime(e.target.value)) {
                      setEndTime('');
                    }
                  }}
                  inputMode="numeric"
                  maxLength={5}
                  pattern="^([01]\\d|2[0-3]):([0-5]\\d)$"
                  title="Use 24-hour format HH:MM"
                  className="time-input"
                />
              </>
            )}
            <button>🔍</button>
          </div>
        </div>

        {/* Right Section with Video Tour and SIEMENS */}
        <div className="header-right">
          {wizardActive && (
            <button
              className="agenda-wizard-btn"
              onClick={() => setWizardOpen(true)}
              title="Build your personalized agenda"
            >
              🪄 Agenda Wizard
            </button>
          )}
          {videoTourUrl && (
            <a
              href={videoTourUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="video-tour-link"
              title="Platform Video Tour"
              onClick={(e) => {
                if (!videoTourUrl || videoTourUrl === '') {
                  e.preventDefault();
                  alert('Video tour URL not configured');
                }
              }}
            >
              <img src={videoIcon} alt="Video Tour" className="video-icon-svg" />
            </a>
          )}
          <div className="siemens-logo">SIEMENS</div>
          <UserMenu />
        </div>
      </header>

      {/* Sticky Navbar Wrapper — tab nav + segment bar + filter panel stick together */}
      <div className="sticky-nav-wrapper">
        {/* Tab Navigation */}
        <TabNavigation
          activeTab={activeTab}
          onTabChange={handleTabChange}
          quarterlyEvents={quarterlyEvents}
          selectedEventDate={selectedEventDate}
          onEventDateChange={handleEventDateChange}
        />

        {/* Segment Filter Bar - Hidden on archived tab until event selected */}
        {!(activeTab === 'archived' && !selectedEventDate) && (
          <>
            <div className="segment-bar">
              <div
                className={selectedSegment === null ? 'active' : ''}
                onClick={() => handleSegmentChange(null)}
              >
                All Segments
              </div>
              {(() => {
                // For live tab, use active event segments; for archived, determine from selected event
                let enabledSegs = null;
                if (activeTab === 'archived' && selectedEventDate) {
                  const archEvt = quarterlyEvents.find(e => e.eventDate === selectedEventDate);
                  enabledSegs = archEvt?.enabledSegments;
                } else if (activeEvent) {
                  enabledSegs = activeEvent.enabledSegments;
                }
                const segments = [
                  { code: 'OPS_SW', label: 'OPS-SW' },
                  { code: 'GROW_PLM', label: 'Grow@PLM' },
                  { code: 'LCS', label: 'LCS' },
                  { code: 'ME', label: 'ME' },
                  { code: 'PES', label: 'PES' },
                  { code: 'SIM', label: 'SIM' },
                ];
                return segments
                  .filter(s => isSegmentEnabled(s.code, enabledSegs))
                  .map(s => (
                    <div
                      key={s.code}
                      className={selectedSegment === s.code ? 'active' : ''}
                      onClick={() => handleSegmentChange(s.code)}
                    >
                      {s.label}
                    </div>
                  ));
              })()}
            </div>

            {/* Filter Instruction */}
            <div className="filter-instruction">
              Click a category to filter {activeTab === 'live' ? 'Live' : activeTab === 'recommended' ? 'Recommended' : 'Archived'} Sessions
            </div>
          </>
        )}

        {/* Category Filter - Hidden on archived tab until event selected */}
        {!(activeTab === 'archived' && !selectedEventDate) && (
          <FilterPanel
            selectedCategory={selectedCategory}
            onCategoryChange={handleCategoryChange}
          />
        )}
      </div>{/* end sticky-nav-wrapper */}

      {/* Learning Day Event Ribbon - Only shown when an active event exists */}
      {!(activeTab === 'archived' && !selectedEventDate) && (() => {
        // When on archive tab, show the selected archived event's details
        if (activeTab === 'archived' && selectedEventDate) {
          const archivedEvent = quarterlyEvents.find(e => e.eventDate === selectedEventDate);
          if (archivedEvent) {
            return (
              <EventRibbon
                title={archivedEvent.eventName || 'Learning Day'}
                date={formatEventDate(archivedEvent.eventDate)}
              />
            );
          }
          return null;
        }
        // Otherwise show the active event
        if (activeEvent) {
          return (
            <EventRibbon
              title={activeEvent.eventName || 'Learning Day'}
              date={formatEventDate(activeEvent.eventDate)}
            />
          );
        }
        return null;
      })()}

      {/* Lightweight Mode Banner */}
      {lightweightMode && (
        <div className="lightweight-mode-banner">
          ⚡ Lightweight Mode Active - Presenter photos disabled for faster loading
        </div>
      )}

      {/* Timezone Selector - Hidden on archived tab until event selected */}
      {!(activeTab === 'archived' && !selectedEventDate) && (
        <TimeZoneSelector
          selectedTimezone={selectedTimezone}
          onTimezoneChange={handleTimezoneChange}
        />
      )}

      {/* Main Content Grid - Schedule Layout */}
      <main className="content-grid">
        {loading ? (
          <div className="loading">Loading sessions...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : activeTab === 'archived' && !selectedEventDate ? (
          <div className="no-results">
            Please select an archived event date from the dropdown above to view sessions.
          </div>
        ) : deduplicatedSessions.length === 0 ? (
          <div className="no-results">
            {activeEvent === null && (activeTab === 'live' || activeTab === 'recommended') &&
              !selectedSegment && !selectedCategory && !searchQuery && !startTime && !endTime ? (
              'Stay Tuned ! New Live Sessions are on the way , visit this page later for Learning Day registration'
            ) : (
              'No sessions found matching your filters.'
            )}
          </div>
        ) : activeTab === 'archived' ? (
          <ArchivedSessionsTable sessions={deduplicatedSessions} />
        ) : (
          <div
            className="schedule-grid"
            style={{
              gridTemplateRows: `repeat(${timeSlots.length}, 320px)`,
              gridTemplateColumns: `100px repeat(${Math.max(maxColumns, 1)}, minmax(280px, 400px))`
            }}
          >
            {/* Time slot labels with timezone conversion */}
            {timeSlots.map((slot, idx) => {
              const targetZone = selectedTimezone === 'local' ? 'local'
                : ['CET', 'Central Europe', 'cet'].includes(selectedTimezone) ? 'Europe/Paris'
                  : selectedTimezone;
              const converted = slot.timestamp.setZone(targetZone);
              const displayTimestamp = converted.isValid ? converted : slot.timestamp;
              const displayTime = displayTimestamp.toFormat('HH:mm');
              const displayDate = displayTimestamp.toFormat('dd MMM');
              return (
                <div
                  key={`slot-${slot.minutes}`}
                  className="schedule-time-label"
                  style={{ gridRow: idx + 1, gridColumn: 1 }}
                >
                  <span className="schedule-time-label-time">{displayTime}</span>
                  {displayDate && <span className="schedule-time-label-date">{displayDate}</span>}
                </div>
              );
            })}

            {/* Session cards positioned in the grid */}
            {sessionPlacements.map(session => {
              // Span the correct number of 30-min grid rows for the session duration
              const durationMinutes = session.endMins - session.startMins;
              const rowSpan = Math.max(1, Math.ceil(durationMinutes / 30));

              return (
                <div
                  key={session.id}
                  className="schedule-session-wrapper"
                  style={{
                    gridRow: `${session.startSlot + 1} / span ${rowSpan}`,
                    gridColumn: session.column + 2
                  }}
                >
                  <SessionCard
                    session={session}
                    timezone={selectedTimezone}
                    isArchived={activeTab === 'archived'}
                    lightweightMode={lightweightMode}
                    isGrowPLM={session.segment !== 'GROW_PLM' && session.segment !== 'Grow@PLM' && growPLMSessionKeys.has(`${session.name}||${session.presenter}||${session.timeCET}||${session.date}`)}
                    showSegmentPrefix={!selectedSegment}
                  />
                </div>
              );
            })}
          </div>
        )}
      </main>
      {/* <SurveyPopup activeEventId={activeEvent?.id || null} /> */}
      <VideoPopup />
      {wizardActive && (
        <AgendaWizard
          isOpen={wizardOpen}
          onClose={() => setWizardOpen(false)}
          sessions={sessions}
          categories={categoryList}
          categoryColors={categoryColors}
        />
      )}
    </div>
  );
};

export default Dashboard;
